import type zh from '../zh/settings';

/**
 * Frozen copy of the settings text the UI showed before the zh/en switch, rendered while
 * UI_I18N_ENABLED is off (gamma / prod). Do not edit the text; add a key here only when
 * zh gains one, with the text the UI would show without the switch. Delete with the flag.
 */
const settings: typeof zh = {
  account: {
    title: 'Account Information & Setting',
    infoHeading: 'Account Information',
    name: 'Account Name',
    login: 'Login',
    activeAgent: 'Active Agent',
    role: 'Role',
    admin: 'Admin',
    editInBackend: 'Edit in Backend',
    tabsLabel: 'Account settings sections',
    tabCapabilities: 'Capabilities',
    tabMemory: 'Memory',
    tabKnowledge: 'Knowledge',
  },
  capabilities: {
    heading: 'MCP Services',
    tools: '{{n}} tools',
    write: '{{n}} write',
    read: '{{n}} read',
    connected: 'Connected',
  },
  knowledge: {
    intro: 'The knowledge the agent draws on when it answers. The platform maintains it.',
    agent: 'Agent',
    loading: 'Loading…',
    loadFailed: 'Could not load the knowledge bases.',
    retry: 'Retry',
    forbidden: "You don't have access to this agent.",
    empty: 'This agent has no knowledge base yet.',
    documents: 'Documents: {{n}}',
    lastSynced: 'Last synced: {{time}}',
    status: { succeeded: 'Synced', syncing: 'Syncing', failed: 'Sync failed' },
    failedHint: 'The last sync failed. The agent still uses the content from the last successful sync.',
    domainsTitle: 'Domains',
    domainsIntro: 'Curated, structured knowledge (definitions, how to query, business rules) that the agent reads as needed.',
    kbTitle: 'Knowledge bases',
    kbIntro: 'Documents the agent searches when it answers, synced regularly.',
    domainsEmpty: 'This agent has no domains yet.',
    domainsLoadFailed: 'Could not load the domains.',
    lastPublished: 'Last published: {{time}}',
    scope: { agent: 'Agent-specific', shared: 'Shared' },
  },
  memory: {
    agent: 'Agent',
    type: 'Type',
    category: 'Category',
    typeAll: 'All',
    typeLtm: 'Long-term',
    typeEpisodic: 'Episodic',
    typeReflection: 'Reflection',
    categoryAll: 'All',
    refresh: 'Refresh',
    loadingShort: 'Loading…',
    loading: 'Loading memory…',
    loadFailed: 'Failed to load memory',
    empty: 'No memory records for this agent yet.',
    noContent: '(no content)',
    loadMore: 'Load more',
  },
  access: {
    breadcrumbHome: 'Rocky',
    breadcrumbRbms: 'Rocky Backend Management System',
    breadcrumbAccess: 'Access Control',
    title: '{{agent}} Access Control',
    agentLabel: 'Agent',
    kinds: {
      user: { label: 'Person', idLabel: 'Alias', idHint: 'The Amazon alias to grant (e.g. jiananj). Takes effect immediately.' },
      team: {
        label: 'Team',
        idLabel: 'Abacus team id',
        idHint: 'amzn1.abacus.team.… — every team member is admitted. Membership syncs within 24 hours.',
      },
      ldap: { label: 'LDAP group', idLabel: 'LDAP group', idHint: 'Members are admitted immediately via their login token.' },
      posix: { label: 'POSIX group', idLabel: 'POSIX group', idHint: 'Membership syncs within 24 hours.' },
    },
    anyType: 'Any type',
    anyAccess: 'Any access',
    canManage: 'Can manage',
    useOnly: 'Use only',
    forbidden: "You don't have manage access on this agent.",
    unreachable: 'Unable to reach the server. Check your connection.',
    saveFailed: 'Failed to save the grant.',
    revokeFailed: 'Failed to revoke access.',
    updatedHeader: 'Access updated',
    grantedHeader: 'Access granted',
    updatedBody: 'The grant was updated.',
    savedBody: 'The grant was saved.',
    errorHeader: 'Something went wrong',
    columns: {
      name: 'Name',
      type: 'Type',
      access: 'Access',
      grantedBy: 'Granted by',
      grantedAt: 'Granted',
      expiresAt: 'Expires',
      action: 'Action',
    },
    manage: 'Manage',
    use: 'Use',
    edit: 'Edit',
    remove: 'Remove',
    removeSelected: 'Remove ({{n}})',
    emptyTitle: 'No access grants',
    emptyBody: 'No teams, groups or people have been granted access to this agent yet.',
    noMatchTitle: 'No matches',
    noMatchBody: 'No grants match your search.',
    loading: 'Loading access list…',
    tableTitle: 'Access grants',
    tableDescription: 'Who may use this agent, and who may manage its access. Remove a row to revoke.',
    infoHeader: 'Access control',
    infoBody:
      'Every row is a grant. Use lets the principal select and chat with the agent; Manage additionally lets them edit this list. Person and LDAP-group grants take effect immediately; Team and POSIX-group membership syncs within 24 hours. Removing a grant takes effect immediately.',
    info: 'Info',
    addGrant: 'Add grant',
    search: 'Find by name or id',
    matches: '{{n}} matches',
    filterType: 'Filter by principal type',
    filterAccess: 'Filter by access level',
    preferences: 'Preferences',
    confirm: 'Confirm',
    cancel: 'Cancel',
    pageSize: 'Page size',
    editTitle: 'Edit access — {{name}}',
    save: 'Save',
    grantAccess: 'Grant access',
    formType: 'Type',
    displayName: 'Display name',
    displayNameHint: 'Optional label shown in this list instead of the id.',
    formAccess: 'Access',
    useDescription: 'Can select and chat with this agent.',
    manageDescription: 'Can use the agent and edit who has access to it.',
  },
  rbms: {
    title: 'Rocky Backend Management System',
    subtitle: 'Central console for managing agent access and workspace homepages across the Rocky platform.',
    deniedTitle: 'Access Denied',
    deniedBody: 'You need to be a member of <code>{{group}}</code> to access this page.',
    accessTitle: 'Access Control',
    accessDescription: 'Manage who can view and edit each agent — add groups or people, assign roles, and review member status.',
    homepageTitle: 'Edit Workspace Homepage',
    homepageDescription:
      'Configure the agent workspace homepage — page header, hot questions, and hot topic cards shown to users.',
    cpaTitle: 'CPA',
    cpaDescription:
      '配置 Credit Program Advisor 的决策树、项目参数、资格规则与叠加关系，并管理发布版本。',
  },
  cpa: {
    tabTree: '决策树',
    tabPrograms: '项目参数',
    tabRules: '规则',
    tabStacking: '叠加关系',
    tabVersions: '版本',
  },
  login: {
    brand: 'Rocky Agent',
    authenticating: 'Authenticating',
    resuming: 'Resuming',
    welcome: 'Welcome',
    failed: 'Authentication Failed',
    signingIn: 'Signing you in...',
    signedIn: 'Signed in successfully',
    resumingSession: 'Resuming your agent session...',
    signedInAs: 'Signed in as <strong>{{user}}</strong>',
    success: 'Authentication successful',
    canClose: 'You can close this window now.',
    tryAgain: 'Try again',
    footer: 'Secure authentication via Federate',
    errors: {
      noToken: 'No identity token available. Please log in again.',
      noState: 'No authentication state found. Please try signing in again from Slack.',
      missing_state: 'The authentication link appears to be incomplete. Please try signing in again from Slack.',
      session_expired: 'This authentication link has expired or has already been used. Please go back to Slack and try again.',
      already_consumed: 'This authentication link has already been used. Please go back to Slack and try again.',
      unknown_channel: 'Could not find the requested agent. Please contact the administrator.',
      store_unavailable: 'The service is temporarily unavailable. Please try again in a moment.',
      auth_failed: 'Authentication failed. Please try again.',
      resumeFailed: 'Resume failed',
      unexpected: 'Failed to resume agent session. Please try again.',
    },
  },
  about: {
    title: "Rocky is providing incorrect or misleading responses. What's going on?",
    lede: 'Rocky is an AI agent. Like all generative AI assistants, it tries hard to be helpful — but it can sometimes produce information that is incomplete, out of date, or simply wrong. This page explains why that happens and how to get the most reliable results.',
    whyHeading: 'Why Rocky can produce incorrect responses',
    why1: '<strong>Hallucination.</strong> Rocky can generate text that looks confident and authoritative but isn\'t factually grounded. This is a known limitation of today\'s frontier generative AI models — output that “looks correct but is mistaken.”',
    why2: "<strong>Knowledge and data limits.</strong> Rocky's underlying model has a training cutoff and may not know about recent changes. When it draws on connected tools, skills, or internal data sources, its answer is only as current and complete as those sources.",
    why3: '<strong>Tool and retrieval gaps.</strong> Rocky may call tools or search internal systems to answer you. If a tool returns partial results, times out, or lacks permission to the data you need, the response can be incomplete without Rocky flagging it.',
    why4: "<strong>Ambiguity.</strong> If a request is open to interpretation, Rocky may guess your intent and answer confidently in a direction you didn't mean.",
    doHeading: 'What you should do',
    do1: "<strong>Don't treat Rocky as a single source of truth.</strong> Verify anything high-stakes — especially before acting on it or sharing it with others.",
    do2: '<strong>Check the sources and tool output.</strong> When Rocky cites a document, ticket, or system, open it directly. The original may hold context the summary leaves out.',
    do3: '<strong>Ask Rocky to show its work.</strong> Request the steps, the tools it used, or the specific records it relied on so you can confirm them.',
    do4: '<strong>Give feedback.</strong> Use the feedback controls on a response to flag anything unhelpful or incorrect. Your reports help improve Rocky.',
    footnote: 'Rocky is provided to help you work faster — not to replace your own judgment. When accuracy matters, confirm before you rely on it.',
  },
  homepageEditor: {
    englishHint: 'English is optional. Anything left empty shows the Chinese text.',
    editingFor: 'Editing homepage for: <strong>{{agent}}</strong>',
    loading: 'Loading configuration…',
    part: 'Part {{n}}',
    edit: 'Edit',
    badgeNew: 'NEW',
    badgeHot: 'HOT',
    conflict:
      'This homepage was just updated by someone else. The latest version has been reloaded — please re-apply your changes and publish again.',
    reloadFailed: 'Failed to reload the latest configuration. Please refresh the page and try again.',
    saveFailed: 'Save failed. Please try again.',
    cancel: 'Cancel',
    publish: 'Publish',
    optional: '- optional',
    maxWords: 'max. {{n}} words',
    maxWordsLines: 'max. {{n}} words, {{lines}} lines',
    tooManyLines: 'Up to {{n}} lines. Remove the extra line breaks.',
    fontWeight: 'Font weight',
    weightBold: 'Bold',
    weightRegular: 'Regular',
    linkDisabled: 'Link (not configurable here)',
    textColor: 'Text color',
    color: 'Color',
    setColor: 'Set color',
    colorNone: 'None',
    hex: 'Hex',
    hexValue: 'Hex color value',
    pageHeader: 'Page Header',
    subheading: 'Subheading',
    badge: 'Badge:',
    addQuestion: 'Add New Question',
    question: 'Hot Question {{n}}',
    removeQuestion: 'Remove question {{n}}',
    moveQuestionUp: 'Move question {{n}} up',
    moveQuestionDown: 'Move question {{n}} down',
    dragQuestion: 'Drag to reorder question {{n}}',
    uiCopy: 'UI Copy',
    uiCopyPlaceholder: '相关问题推荐相关问题推荐',
    prompt: 'Prompt',
    addTopic: 'Add New Topic',
    topic: 'Hot Topics {{n}}',
    removeTopic: 'Remove topic {{n}}',
    moveTopicUp: 'Move topic {{n}} up',
    moveTopicDown: 'Move topic {{n}} down',
    dragTopic: 'Drag to reorder topic {{n}}',
    iconImage: 'Icon Image',
    topicTitle: 'Topic Title',
    topicTitlePlaceholder: '我的 quota 能完成吗？',
    description: 'Description Text',
    link: 'Link',
    linkHint:
      'When set, clicking the card opens this link instead of sending the Prompt. Site path (e.g. /cheetah) or http(s):// URL.',
    linkLabel: 'Topic {{n}} link',
    linkError: 'Enter a site path starting with / (e.g. /cheetah) or a full http(s):// URL.',
    chooseFiles: 'Choose files',
    uploading: 'Uploading…',
    removeFile: 'Remove file',
    icon: {
      type: '仅支持 PNG / JPEG / WebP 图片',
      tooLarge: '文件过大({{kb}} KB),需 ≤ 512 KB',
      unreadable: '无法读取图片,文件可能已损坏',
      tooSmall: '尺寸过小({{w}}×{{h}}),至少 {{min}}×{{min}}',
      notSquare: '图片非正方形({{w}}×{{h}}),显示时可能被裁切',
      uploadFailed: '上传失败,请重试',
    },
  },
};

export default settings;
